import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Tabs, Select, Input, Button, Tag, Table, Typography, Badge, Alert, message } from 'antd';
import {
  PauseOutlined, CaretRightOutlined, DeleteOutlined, SearchOutlined,
  SyncOutlined, VerticalAlignBottomOutlined
} from '@ant-design/icons';
import { usePageHeader } from '../../layouts/AdminLayout';
import BackendLogsService from '../../services/BackendLogsService';

const { Text, Paragraph } = Typography;

const MAX_LINES = 2000;
const FLUSH_INTERVAL_MS = 300;
const RECONNECT_DELAY_MS = 5000;
const ERRORS_REFRESH_MS = 60000;

const LEVEL_OPTIONS = [
  { value: 'error', label: 'Error' },
  { value: 'warn', label: 'Warn' },
  { value: 'info', label: 'Info' },
  { value: 'http', label: 'HTTP' },
  { value: 'debug', label: 'Debug' },
];

const LEVEL_COLORS = {
  error: '#f87171',
  warn: '#fbbf24',
  info: '#9ca3af',
  http: '#a78bfa',
  debug: '#6b7280',
  verbose: '#6b7280',
  silly: '#6b7280',
};

const STATUS_TAGS = {
  live: { color: 'success', label: 'Live' },
  paused: { color: 'warning', label: 'Paused' },
  connecting: { color: 'processing', label: 'Connecting…' },
  disconnected: { color: 'error', label: 'Disconnected — retrying' },
};

const terminalStyle = {
  background: '#0d1117',
  color: '#e5e7eb',
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, "Liberation Mono", monospace',
  fontSize: 12,
  lineHeight: '20px',
  height: 'calc(100vh - 300px)',
  minHeight: 400,
  overflowY: 'auto',
  borderRadius: 8,
  padding: '8px 12px',
};

const formatTime = (ts) => {
  const d = new Date(ts);
  return `${d.toLocaleTimeString([], { hour12: false })}.${String(d.getMilliseconds()).padStart(3, '0')}`;
};

const matchesFilters = (entry, { level, source, search }) => {
  if (level && entry.level !== level) return false;
  if (source && !(entry.source || '').toLowerCase().includes(source)) return false;
  if (search) {
    const haystack = `${entry.message} ${entry.details || ''}`.toLowerCase();
    if (!haystack.includes(search)) return false;
  }
  return true;
};

const LogLine = React.memo(function LogLine({ entry }) {
  return (
  <div style={{ display: 'flex', gap: 16 }}>
    <span style={{ color: '#6b7280', flexShrink: 0, width: 96 }}>{formatTime(entry.ts)}</span>
    <span style={{ color: LEVEL_COLORS[entry.level] || '#9ca3af', flexShrink: 0, width: 44 }}>
      {entry.level.toUpperCase()}
    </span>
    <span
      style={{
        flex: 1,
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
        color: entry.level === 'error' ? '#fca5a5' : entry.level === 'warn' ? '#fde68a' : '#e5e7eb',
      }}
    >
      {entry.message}
      {entry.details && <span style={{ color: '#9ca3af' }}> {entry.details}</span>}
    </span>
  </div>
  );
});

const LiveTail = () => {
  const [entries, setEntries] = useState([]);
  const [paused, setPaused] = useState(false);
  const [status, setStatus] = useState('connecting');
  const [level, setLevel] = useState();
  const [source, setSource] = useState('');
  const [search, setSearch] = useState('');
  const [autoScroll, setAutoScroll] = useState(true);

  const incomingRef = useRef([]);
  const pausedRef = useRef(false);
  const lastSeqRef = useRef(0);
  const scrollRef = useRef(null);

  useEffect(() => {
    pausedRef.current = paused;
  }, [paused]);

  useEffect(() => {
    let es;
    let retryTimer;
    let cancelled = false;

    const connect = () => {
      setStatus('connecting');
      es = BackendLogsService.createStream(lastSeqRef.current);
      es.onopen = () => setStatus('live');
      es.onmessage = (evt) => {
        try {
          const entry = JSON.parse(evt.data);
          lastSeqRef.current = entry.seq;
          incomingRef.current.push(entry);
          if (incomingRef.current.length > MAX_LINES) {
            incomingRef.current.splice(0, incomingRef.current.length - MAX_LINES);
          }
        } catch {
          // Ignore malformed frames
        }
      };
      es.onerror = () => {
        if (es.readyState === EventSource.CLOSED) {
          es.close();
          setStatus('disconnected');
          if (!cancelled) retryTimer = setTimeout(connect, RECONNECT_DELAY_MS);
        } else {
          setStatus('connecting');
        }
      };
    };

    connect();
    return () => {
      cancelled = true;
      clearTimeout(retryTimer);
      es?.close();
    };
  }, []);

  // Batch incoming lines so a noisy backend doesn't re-render per line.
  useEffect(() => {
    const id = setInterval(() => {
      if (pausedRef.current || incomingRef.current.length === 0) return;
      const batch = incomingRef.current;
      incomingRef.current = [];
      setEntries((prev) => {
        const next = prev.concat(batch);
        return next.length > MAX_LINES ? next.slice(-MAX_LINES) : next;
      });
    }, FLUSH_INTERVAL_MS);
    return () => clearInterval(id);
  }, []);

  const filters = useMemo(() => ({
    level,
    source: source.trim().toLowerCase(),
    search: search.trim().toLowerCase(),
  }), [level, source, search]);

  const visible = useMemo(
    () => entries.filter((e) => matchesFilters(e, filters)),
    [entries, filters]
  );

  useEffect(() => {
    if (autoScroll && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [visible, autoScroll]);

  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    setAutoScroll(el.scrollHeight - el.scrollTop - el.clientHeight < 40);
  };

  const handleClear = () => {
    incomingRef.current = [];
    setEntries([]);
  };

  const statusTag = STATUS_TAGS[paused && status === 'live' ? 'paused' : status];

  return (
    <div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', marginBottom: 12 }}>
        <Select
          allowClear
          placeholder="All levels"
          value={level}
          onChange={setLevel}
          options={LEVEL_OPTIONS}
          style={{ width: 150 }}
        />
        <Input
          allowClear
          placeholder="Source (e.g. CORS)"
          value={source}
          onChange={(e) => setSource(e.target.value)}
          style={{ width: 180 }}
        />
        <Input
          allowClear
          prefix={<SearchOutlined />}
          placeholder="Search..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ width: 240 }}
        />
        <Button
          icon={paused ? <CaretRightOutlined /> : <PauseOutlined />}
          onClick={() => setPaused((p) => !p)}
        >
          {paused ? 'Resume' : 'Pause'}
        </Button>
        <Button icon={<DeleteOutlined />} onClick={handleClear}>Clear</Button>
        <Tag color={statusTag.color} style={{ marginInlineEnd: 0 }}>{statusTag.label}</Tag>
        <div style={{ flex: 1 }} />
        <Text type="secondary" style={{ fontSize: 12 }}>
          {visible.length.toLocaleString()} of {entries.length.toLocaleString()} lines
        </Text>
      </div>

      <div style={{ position: 'relative' }}>
        <div ref={scrollRef} onScroll={handleScroll} style={terminalStyle}>
          {visible.length === 0 ? (
            <div style={{ color: '#6b7280', padding: '24px 0', textAlign: 'center' }}>
              {entries.length === 0 ? 'Waiting for log output…' : 'No lines match the current filters.'}
            </div>
          ) : (
            visible.map((entry) => <LogLine key={entry.seq} entry={entry} />)
          )}
        </div>
        {!autoScroll && (
          <Button
            size="small"
            type="primary"
            icon={<VerticalAlignBottomOutlined />}
            onClick={() => setAutoScroll(true)}
            style={{ position: 'absolute', right: 20, bottom: 16 }}
          >
            Jump to latest
          </Button>
        )}
      </div>
    </div>
  );
};

const renderErrorDetails = (record) => {
  let parsed = null;
  if (record.details) {
    try {
      parsed = JSON.parse(record.details);
    } catch {
      parsed = null;
    }
  }
  const { stack, ...rest } = parsed || {};
  const hasRest = parsed && Object.keys(rest).length > 0;

  return (
    <pre
      style={{
        ...terminalStyle,
        height: 'auto',
        minHeight: 0,
        maxHeight: 400,
        margin: 0,
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
      }}
    >
      <span style={{ color: '#fca5a5' }}>{record.message}</span>
      {stack && <>{'\n\n'}<span style={{ color: '#9ca3af' }}>{stack}</span></>}
      {hasRest && <>{'\n\n'}{JSON.stringify(rest, null, 2)}</>}
      {!parsed && record.details && <>{'\n\n'}{record.details}</>}
    </pre>
  );
};

const ErrorsTab = ({ onCount }) => {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await BackendLogsService.getErrors(24);
      setItems(res.data || []);
      setTotal(res.total || 0);
      onCount?.(res.total || 0);
    } catch (err) {
      message.error(err.message || 'Failed to load errors');
    } finally {
      setLoading(false);
    }
  }, [onCount]);

  useEffect(() => {
    load();
    const id = setInterval(load, ERRORS_REFRESH_MS);
    return () => clearInterval(id);
  }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((i) =>
      `${i.source || ''} ${i.message || ''} ${i.details || ''}`.toLowerCase().includes(q)
    );
  }, [items, search]);

  const columns = [
    {
      title: 'Time',
      dataIndex: 'ts',
      key: 'ts',
      width: 190,
      render: (ts) => new Date(ts).toLocaleString(),
    },
    {
      title: 'Source',
      dataIndex: 'source',
      key: 'source',
      width: 160,
      render: (s) => <Tag color="red">{s || 'app'}</Tag>,
    },
    {
      title: 'Message',
      dataIndex: 'message',
      key: 'message',
      render: (msg) => (
        <Paragraph
          ellipsis={{ rows: 2 }}
          style={{ marginBottom: 0, fontFamily: 'ui-monospace, Menlo, Consolas, monospace', fontSize: 12 }}
        >
          {msg}
        </Paragraph>
      ),
    },
    {
      title: 'Host',
      dataIndex: 'host',
      key: 'host',
      width: 150,
      render: (h) => <Text type="secondary" style={{ fontSize: 12 }}>{h || '-'}</Text>,
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', marginBottom: 12 }}>
        <Input
          allowClear
          prefix={<SearchOutlined />}
          placeholder="Search errors..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ width: 280 }}
        />
        <Button icon={<SyncOutlined />} onClick={load} loading={loading}>Refresh</Button>
        <div style={{ flex: 1 }} />
        <Text type="secondary" style={{ fontSize: 12 }}>
          {total.toLocaleString()} error{total === 1 ? '' : 's'} in the last 24 hours
          {total > items.length ? ` (showing latest ${items.length.toLocaleString()})` : ''}
        </Text>
      </div>

      {!loading && total === 0 && (
        <Alert type="success" showIcon message="No backend errors in the last 24 hours." style={{ marginBottom: 12 }} />
      )}

      <Table
        rowKey="_id"
        size="small"
        loading={loading && items.length === 0}
        dataSource={filtered}
        columns={columns}
        expandable={{ expandedRowRender: renderErrorDetails, expandRowByClick: true }}
        pagination={{ pageSize: 50, showSizeChanger: false }}
      />
    </div>
  );
};

const BackendLogs = () => {
  const { setPageHeader } = usePageHeader();
  const [activeTab, setActiveTab] = useState('live');
  const [errorCount, setErrorCount] = useState(0);

  useEffect(() => {
    setPageHeader({
      breadcrumbs: [
        { label: 'Home', path: '/admin/dashboard' },
        { label: 'Backend Logs' },
      ],
    });
  }, [setPageHeader]);

  return (
    <div style={{ padding: '0 8px' }}>
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={[
          { key: 'live', label: 'Live Tail', children: <LiveTail /> },
          {
            key: 'errors',
            label: (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                Errors (24h)
                {errorCount > 0 && <Badge count={errorCount} overflowCount={999} size="small" />}
              </span>
            ),
            children: <ErrorsTab onCount={setErrorCount} />,
            forceRender: true,
          },
        ]}
      />
    </div>
  );
};

export default BackendLogs;
